/*
 * Copyright (c) 2025 Bolmate B.V.
 */

import './style/customVat.scss';

import {ChangeFunction, ElementPosition, ErrorType, WithBackground} from '@dvrd/dvr-controls';
import React, {ForwardedRef, forwardRef, useImperativeHandle, useRef, useState} from 'react';
import OrderItem from '../../../models/orderItemModel';
import {ActivateHandle, DefaultVATValue} from '../../../util/interfaces';
import {DefaultVAT} from '../../../util/vatUtil';
import {BolmateButton, BolmateNumberInput} from '../../controls';

interface Props {
    onSubmit: (id: string | Array<string> | OrderItem, countryCode: string) => ChangeFunction<DefaultVATValue>;
}

function CustomVat(props: Props, ref: ForwardedRef<ActivateHandle>) {
    const {onSubmit} = props;
    const [value, setValue] = useState('0');
    const [error, setError] = useState<ErrorType>(null);
    const [active, setActive] = useState(false);
    const productId = useRef<string | Array<string> | OrderItem>('');
    const countryCode = useRef('');

    function onActivate(_countryCode: string, _productId: string | Array<string> | OrderItem) {
        productId.current = _productId;
        countryCode.current = _countryCode;
        setActive(true);
    }

    function onClose() {
        productId.current = '';
        countryCode.current = '';
        setActive(false);
        setValue('0');
        setError(null);
    }

    function onChange(value: string) {
        onResetError();
        setValue(value);
    }

    function onSubmitValue() {
        const numberValue: number = Number(value);
        if (Number.isNaN(numberValue) || numberValue < 0 || numberValue > 100)
            return setError('Vul een geldig percentage in');
        const vatValue = DefaultVAT(numberValue);
        onSubmit(productId.current, countryCode.current)(vatValue);
        onClose();
    }

    function onResetError() {
        setError(null);
    }

    useImperativeHandle(ref, () => ({
        activate: onActivate,
    }));

    return (
        <WithBackground active={active} onClose={onClose}>
            <div className='custom-vat'>
                <BolmateNumberInput onChange={onChange} value={value} label='Aangepast tarief' onEnter={onSubmitValue}
                                    error={error} onFocus={onResetError}
                                    ornaments={{element: <span>%</span>, placement: ElementPosition.RIGHT}}
                                    inputProps={{min: 0, max: 100}} autoFocus autoSelect/>
                <BolmateButton onClick={onSubmitValue} label='Oké'/>
            </div>
        </WithBackground>
    )
}

export default forwardRef<ActivateHandle, Props>(CustomVat);